/**
 * Data Quality Utilities
 *
 * Type guards and formatters for safe data display.
 * Prevents NaN values and provides consistent formatting across the application.
 *
 * Reference: PRP-001 Frontend Data Quality Fixes - Task 6
 */

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a value is a safe number
 * (not NaN, not Infinity, not null/undefined)
 */
export const isSafeNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
};

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a number as currency with fallback for invalid values
 *
 * @param value - The number to format
 * @param currency - Currency code (default: USD)
 * @param fallback - Fallback string if value is invalid (default: '0.00')
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(10) // => '$10.00'
 * formatCurrency(NaN) // => '0.00'
 * formatCurrency(null) // => '0.00'
 * formatCurrency(undefined) // => '0.00'
 */
export const formatCurrency = (
  value: number | undefined | null,
  currency: string = 'USD',
  fallback: string = '0.00'
): string => {
  if (!isSafeNumber(value)) return fallback;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

/**
 * Format a number with locale-aware separators
 *
 * @param value - The number to format
 * @param fallback - Fallback string if value is invalid (default: '0')
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1000) // => '1,000'
 * formatNumber(1234567) // => '1,234,567'
 * formatNumber(NaN) // => '0'
 */
export const formatNumber = (value: number | undefined | null, fallback: string = '0'): string => {
  if (!isSafeNumber(value)) return fallback;
  return value.toLocaleString('en-US');
};

/**
 * Get a safe number with fallback for invalid values
 *
 * @param value - The number to check
 * @param fallback - Fallback number if value is invalid (default: 0)
 * @returns Safe number value
 *
 * @example
 * safeNumber(10) // => 10
 * safeNumber(NaN) // => 0
 * safeNumber(null, 100) // => 100
 */
export const safeNumber = (value: number | undefined | null, fallback: number = 0): number => {
  return isSafeNumber(value) ? value : fallback;
};

// ============================================================================
// User Display Formatters
// ============================================================================

/**
 * Extract user initials from first and last name
 *
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param fallback - Fallback string if names not available (default: 'U')
 * @returns User initials (1-2 uppercase letters)
 *
 * @example
 * extractInitials('John', 'Doe') // => 'JD'
 * extractInitials('josef', 'salyer') // => 'JS'
 * extractInitials('John', null) // => 'J'
 * extractInitials(null, null) // => 'U'
 */
export const extractInitials = (
  firstName: string | undefined | null,
  lastName: string | undefined | null,
  fallback: string = 'U'
): string => {
  const first = firstName?.trim().charAt(0).toUpperCase() || '';
  const last = lastName?.trim().charAt(0).toUpperCase() || '';

  if (first && last) return `${first}${last}`;
  if (first) return first;
  if (last) return last;
  return fallback;
};

/**
 * Get user display name from available name fields
 *
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param email - User's email address
 * @param fallback - Fallback string if all fields missing (default: 'Unknown User')
 * @returns User display name
 *
 * @example
 * getDisplayName('John', 'Doe', null) // => 'John Doe'
 * getDisplayName('John', null, null) // => 'John'
 * getDisplayName(null, null, 'john@example.com') // => 'john'
 * getDisplayName(null, null, null) // => 'Unknown User'
 */
export const getDisplayName = (
  firstName: string | undefined | null,
  lastName: string | undefined | null,
  email: string | undefined | null,
  fallback: string = 'Unknown User'
): string => {
  // Trim and check for non-empty strings
  const first = firstName?.trim();
  const last = lastName?.trim();
  const emailAddr = email?.trim();

  if (first && last) {
    return `${first} ${last}`;
  }
  if (first) return first;
  if (emailAddr) return emailAddr.split('@')[0];
  return fallback;
};
